import type { LevelDefinition } from '../world/LevelDefinition';

const surfaces = {
  floor: { enabled: true, magnetic: true },
  ceiling: { enabled: true, magnetic: true },
  left: { enabled: true, magnetic: true },
  right: { enabled: true, magnetic: true },
  front: { enabled: true, magnetic: true },
  back: { enabled: true, magnetic: true },
};

const ceilingLights = (zs: number[]) =>
  zs.map((z) => ({
    position: [0, 3.9, z] as [number, number, number],
    size: [2.4, 0.08, 1.2] as [number, number, number],
  }));

/**
 * Deck 7 Promenade: two long halls joined by a junction module and a side
 * alcove. It is the first connected multi-zone map.
 */
export const DECK7: LevelDefinition = {
  id: 'deck7',
  name: 'Deck 7 Promenade',
  size: [16, 4, 56],
  surfaces,
  zones: [
    { id: 'promenade-a', origin: [0, 0, 12], size: [8, 4, 24] },
    { id: 'junction', origin: [0, 0, -4], size: [8, 4, 8] },
    { id: 'promenade-c', origin: [0, 0, -20], size: [8, 4, 24] },
    { id: 'alcove', origin: [7, 0, -4], size: [6, 4, 8] },
  ],
  portals: [
    {
      id: 'a-junction',
      a: 'promenade-a',
      b: 'junction',
      axis: 'x',
      position: [0, 0, 0],
      width: 3.2,
      height: 3,
    },
    {
      id: 'junction-c',
      a: 'junction',
      b: 'promenade-c',
      axis: 'x',
      position: [0, 0, -8],
      width: 3.2,
      height: 3,
    },
    {
      id: 'junction-alcove',
      a: 'junction',
      b: 'alcove',
      axis: 'z',
      position: [4, 0, -4],
      width: 2.6,
      height: 3,
    },
  ],
  spawn: { zone: 'promenade-a', surface: 'floor', position: [0, 0, 20], facing: 0 },
  targets: [
    { type: 'drone', zone: 'junction', surface: 'floor', position: [0, 0, -4] },
    { type: 'drone', zone: 'alcove', surface: 'floor', position: [8, 0, -4], magnetic: true },
    { type: 'drone', zone: 'promenade-c', surface: 'floor', position: [0, 0, -18] },
    { type: 'drone', zone: 'promenade-c', surface: 'ceiling', position: [0, 4, -27] },
    { type: 'drone', zone: 'promenade-a', surface: 'left', position: [-4, 2, 8] },
    { type: 'drone', zone: 'junction', surface: 'right', position: [4, 2, -2] },
  ],
  obstacles: [
    // Side buttresses down the first hall: hard cover with gaps between them.
    {
      type: 'box',
      kind: 'pillar',
      zone: 'promenade-a',
      position: [3.4, 2, 19],
      size: [1.1, 4, 1.1],
    },
    {
      type: 'box',
      kind: 'pillar',
      zone: 'promenade-a',
      position: [-3.4, 2, 14],
      size: [1.1, 4, 1.1],
    },
    {
      type: 'box',
      kind: 'pillar',
      zone: 'promenade-a',
      position: [3.4, 2, 9],
      size: [1.1, 4, 1.1],
    },
    {
      type: 'box',
      kind: 'pillar',
      zone: 'promenade-a',
      position: [-3.4, 2, 4],
      size: [1.1, 4, 1.1],
    },
    // Jumpable consoles in the junction.
    {
      type: 'box',
      kind: 'console',
      zone: 'junction',
      position: [2.6, 0.5, -2.5],
      size: [2.2, 1, 0.8],
    },
    {
      type: 'box',
      kind: 'console',
      zone: 'junction',
      position: [-2.6, 0.5, -5.5],
      size: [2.2, 1, 0.8],
    },
    // Crates in the far hall.
    {
      type: 'box',
      kind: 'crate',
      zone: 'promenade-c',
      position: [2, 0.6, -16],
      size: [1.2, 1.2, 1.2],
    },
    {
      type: 'box',
      kind: 'crate',
      zone: 'promenade-c',
      position: [-2, 0.6, -24],
      size: [1.2, 1.2, 1.2],
    },
  ],
  style: {
    wallColor: 0x2a3038,
    floorColor: 0x1b2026,
    ceilingColor: 0xc9d6e2,
    pathColor: 0xffb347,
    path: [
      { from: [0, 22], to: [0, 0], width: 2.4 },
      { from: [0, 0], to: [0, -8], width: 2.4 },
      { from: [0, -8], to: [0, -30], width: 2.4 },
      { from: [4, -4], to: [10, -4], width: 2 },
    ],
    ceilingLights: ceilingLights([20, 14, 8, 2, -4, -10, -16, -22, -28]),
  },
  seed: 2049,
};
